import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import './App.css'
import './assets/book-reader.css'
import BookList from './components/BookList';
import Login from './components/Login';
import { ThemeProvider } from './contexts/ThemeContext';
import { WordDataProvider } from './contexts/WordDataContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import BookReader from './components/BookReader';
import { AppInitializer } from './services/AppInitializer';
import { persistentLogger } from './utils/PersistentLogger';
import { AuthService } from './services/AuthService';
import { TooltipProvider } from './components/TooltipProvider';

function App() {
  const [initComplete, setInitComplete] = React.useState(false);
  const [initError, setInitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Initialize app before rendering
    AppInitializer.init()
      .then((result) => {
        if (result.success) {
          setInitComplete(true);
        } else {
          setInitError(result.error || 'Initialization failed');
          setInitComplete(true); // Still render, but show error if needed
        }
      })
      .catch((error) => {
        console.error('[App] Initialization error:', error);
        setInitError(error instanceof Error ? error.message : 'Unknown error');
        setInitComplete(true); // Still render
      });
  }, []);

  // Show loading state while initializing
  if (!initComplete) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Initializing...
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <WordDataProvider>
          <TooltipProvider>
            <AppContent />
          </TooltipProvider>
        </WordDataProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  // Protected route wrapper - defined inside AppContent so it's within AuthProvider
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated } = useAuth();
    
    persistentLogger.log('ProtectedRoute', 'Rendering ProtectedRoute', { 
      isAuthenticated,
      hasToken: !!AuthService.getToken()
    });

    if (!isAuthenticated) {
      persistentLogger.warn('ProtectedRoute', 'User not authenticated - showing Login component');
      return <Login onLoginSuccess={async () => {
        persistentLogger.log('ProtectedRoute', 'onLoginSuccess callback called');
        // Re-initialize after login
        persistentLogger.log('ProtectedRoute', 'Calling AppInitializer.reinitAfterLogin()');
        await AppInitializer.reinitAfterLogin();
        persistentLogger.log('ProtectedRoute', 'AppInitializer.reinitAfterLogin() completed');
        // Check auth state after reinit
        const stillAuthenticated = AuthService.isAuthenticated();
        persistentLogger.log('ProtectedRoute', 'Auth state after reinit', { 
          stillAuthenticated,
          hasToken: !!AuthService.getToken()
        });
        // Update auth state (login was already called in Login component)
        // The state update will cause a re-render and show the protected content
      }} />;
    }

    persistentLogger.log('ProtectedRoute', 'User authenticated - rendering protected content');
    return <>{children}</>;
  };

  return (
    <Router>
      <div className="app-container">
        <Routes>
          <Route path="/" element={
            <ProtectedRoute>
              <BookList />
            </ProtectedRoute>
          } />
          <Route path="/book/:isbn" element={
            <ProtectedRoute>
              <BookReaderWrapper />
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <Toaster position="top-center" richColors />
    </Router>
  );
}

// Wrapper component to handle navigation for book reader
function BookReaderWrapper() {
  const navigate = useNavigate();
  
  const handleClose = () => {
    navigate('/');
  };

  return <BookReader onClose={handleClose} />;
}

export default App
